@layer refine.base {
  :host {
    --_bg: var(--chat-message-bg, var(--color-contrast-100));
    --_color: var(--chat-message-color, var(--color-contrast-900));
    --_user-bg: var(--chat-message-user-bg, var(--color-primary));
    --_user-color: var(--chat-message-user-color, var(--color-primary-content));
    --_radius: var(--chat-message-radius, var(--rounded-lg));
    --_max-width: var(--chat-message-max-width, 36rem);

    display: block;
  }

  .row {
    display: flex;
    gap: var(--size-2-5);
    align-items: flex-start;
  }

  :host([sender='user']) .row {
    flex-direction: row-reverse;
  }

  .avatar {
    display: flex;
    flex-shrink: 0;
  }

  .avatar[hidden] {
    display: none;
  }

  .column {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--size-1);
    align-items: flex-start;
    min-width: 0;
  }

  :host([sender='user']) .column {
    align-items: flex-end;
  }

  .name {
    /* Match `.bubble`'s inline padding so the name lines up with the bubble's own text
       instead of the bubble's outer box. */
    padding-inline: var(--size-3-5);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    color: var(--color-contrast-500);
  }

  .name[hidden] {
    display: none;
  }

  /* =====================
       Bubble
       ===================== */

  .bubble {
    box-sizing: border-box;
    width: fit-content;
    max-width: var(--_max-width);
    padding: var(--size-2-5) var(--size-3-5);
    color: var(--_color);
    background: var(--_bg);
    border-radius: var(--_radius);
  }

  :host([sender='user']) .bubble {
    color: var(--_user-color);
    background: var(--_user-bg);
  }

  .content {
    font-size: var(--text-sm);
    line-height: var(--leading-relaxed);
  }

  /*
   * `white-space`/`overflow-wrap` live on the slot itself, not `.content` — both are
   * inherited properties, and slotted nodes inherit from their point of use (the `<slot>`)
   * per the CSS Scoping spec, so this still reaches the projected text. Scoping it here
   * (instead of the parent `.content`) keeps `.content`'s own whitespace-only text nodes —
   * the newline + indentation between `<slot>` and the cursor `<span>` in this template —
   * under normal collapsing rules, so they don't render as extra blank lines. Putting
   * `pre-wrap` on `.content` directly did exactly that: real bug, caught by measuring
   * rendered box height in a real browser.
   */
  .content-slot {
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  /* =====================
       Streaming cursor
       ===================== */

  .cursor {
    display: inline-block;
    width: 0.5em;
    height: 1em;
    margin-inline-start: var(--size-0-5);
    vertical-align: text-bottom;
    background: currentcolor;
    animation: var(--_motion-animation, chat-message-blink 1s step-end infinite);
  }

  .cursor[hidden] {
    display: none;
  }

  @keyframes chat-message-blink {
    50% {
      opacity: 0;
    }
  }

  /* =====================
       Meta row (timestamp / status / error / retry)
       ===================== */

  .meta {
    display: flex;
    gap: var(--size-1-5);
    align-items: center;

    /* Match `.bubble`'s inline padding — see `.name` above. */
    padding-inline: var(--size-3-5);
    font-size: var(--text-xs);
    color: var(--color-contrast-400);
  }

  .meta[hidden] {
    display: none;
  }

  .timestamp[hidden] {
    display: none;
  }

  .status {
    display: inline-flex;
    align-items: center;
  }

  .status[hidden] {
    display: none;
  }

  .status[data-status='sent'] {
    color: var(--color-success);
  }

  .status[data-status='error'] {
    color: var(--color-error);
  }

  .spinner {
    display: inline-block;
    width: 0.75em;
    height: 0.75em;
    border: 2px solid var(--color-contrast-300);
    border-inline-end-color: transparent;
    border-radius: 50%;
    animation: var(--_motion-animation, chat-message-spin var(--transition-loader) infinite);
  }

  @keyframes chat-message-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .error-text {
    color: var(--color-error);
  }

  .error-text[hidden] {
    display: none;
  }

  .retry {
    padding: 0;
    font: inherit;
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    color: var(--color-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    background: none;
    border: none;
  }

  .retry[hidden] {
    display: none;
  }

  .retry:focus-visible {
    outline: var(--border-2) solid var(--color-focus);
    outline-offset: var(--border-2);
  }

  /* =====================
       Actions
       ===================== */

  .actions {
    display: flex;
    gap: var(--size-1);

    /* Match `.bubble`'s inline padding — see `.name` above. */
    padding-inline: var(--size-3-5);
  }

  .actions[hidden] {
    display: none;
  }
}
